import type { EncodeObject } from '@cosmjs/proto-signing';
import type { SendToEthParams } from '@/types/bridge';

/**
 * Chain fee rate: 20 basis points (0.2%)
 */
export const CHAIN_FEE_RATE = 0.002;

/**
 * Default denom for GRAV token
 */
export const GRAV_DENOM = 'ugraviton';

/**
 * Gravity Bridge contract address on Ethereum mainnet
 */
export const GRAVITY_BRIDGE_CONTRACT = '0xa4108aA1Ec4967F8b52220a4f7e94A8201F2D906';

/**
 * Calculate chain fee (0.2% of bridge amount)
 * @param bridgeAmountUgraviton Amount being bridged in ugraviton
 * @returns Chain fee amount in ugraviton
 */
export function calculateChainFee(bridgeAmountUgraviton: string | undefined): string {
  if (bridgeAmountUgraviton === '' || bridgeAmountUgraviton === undefined || bridgeAmountUgraviton.length === 0) return '0';
  try {
    const amount = BigInt(bridgeAmountUgraviton);
    // Chain fee = amount * 0.002 = amount * 2 / 1000
    const chainFee = (amount * BigInt(2)) / BigInt(1000);
    return chainFee.toString();
  } catch {
    return '0';
  }
}

/**
 * Calculate maximum bridge amount given balance and bridge fee
 * 
 * The relationship is:
 *   balance = bridgeAmount + bridgeFee + chainFee
 *   chainFee = bridgeAmount * 0.002
 * 
 * Solving for bridgeAmount:
 *   bridgeAmount = (balance - bridgeFee) / 1.002
 *   bridgeAmount = (balance - bridgeFee) * 1000 / 1002
 * 
 * @param balanceUgraviton User's balance in ugraviton
 * @param bridgeFeeUgraviton Selected bridge fee in ugraviton
 * @returns Maximum bridge amount in ugraviton
 */
export function calculateMaxBridgeAmount(
  balanceUgraviton: string | undefined,
  bridgeFeeUgraviton: string | undefined
): string {
  if (balanceUgraviton === undefined || balanceUgraviton === '' || bridgeFeeUgraviton === undefined || bridgeFeeUgraviton === '') return '0';
  try {
    const balance = BigInt(balanceUgraviton);
    const bridgeFee = BigInt(bridgeFeeUgraviton);
  
    if (balance <= bridgeFee) {
      return '0';
    }
  
    const available = balance - bridgeFee;
    // max_amount = available / 1.002 = available * 1000 / 1002
    const maxAmount = (available * BigInt(1000)) / BigInt(1002);
    return maxAmount.toString();
  } catch {
    return '0';
  }
}

/**
 * Calculate total cost of a bridge transaction
 * @param bridgeAmountUgraviton Amount being bridged
 * @param bridgeFeeUgraviton Bridge fee amount
 * @returns Total cost in ugraviton (amount + bridge_fee + chain_fee)
 */
export function calculateTotalBridgeCost(
  bridgeAmountUgraviton: string | undefined,
  bridgeFeeUgraviton: string | undefined
): string {
  if (bridgeAmountUgraviton === undefined || bridgeAmountUgraviton === '' || bridgeFeeUgraviton === undefined || bridgeFeeUgraviton === '') return '0';
  try {
    const amount = BigInt(bridgeAmountUgraviton);
    const bridgeFee = BigInt(bridgeFeeUgraviton);
    const chainFee = BigInt(calculateChainFee(bridgeAmountUgraviton));
    return (amount + bridgeFee + chainFee).toString();
  } catch {
    return '0';
  }
}

/**
 * Check if user has insufficient balance for bridge
 * @param balanceUgraviton User's balance
 * @param bridgeAmountUgraviton Amount to bridge
 * @param bridgeFeeUgraviton Bridge fee
 * @returns true if balance is insufficient
 */
export function hasInsufficientBalance(
  balanceUgraviton: string | undefined,
  bridgeAmountUgraviton: string | undefined,
  bridgeFeeUgraviton: string | undefined
): boolean {
  if (balanceUgraviton === undefined || balanceUgraviton === '' || bridgeAmountUgraviton === undefined || bridgeAmountUgraviton === '' || bridgeFeeUgraviton === undefined || bridgeFeeUgraviton === '') return false;
  try {
    const balance = BigInt(balanceUgraviton);
    const totalCost = BigInt(calculateTotalBridgeCost(bridgeAmountUgraviton, bridgeFeeUgraviton));
    return balance < totalCost;
  } catch {
    return false;
  }
}

/**
 * Convert GRAV (whole tokens) to ugraviton (micro-units)
 * @param grav Amount in GRAV
 * @returns Amount in ugraviton
 */
export function gravToUgraviton(grav: string | number): string {
  const amount = typeof grav === 'string' ? parseFloat(grav) : grav;
  if (isNaN(amount)) return '0';
  return Math.floor(amount * 1_000_000).toString();
}

/**
 * Convert ugraviton (micro-units) to GRAV (whole tokens)
 * @param ugraviton Amount in ugraviton
 * @param decimals Number of decimal places
 * @returns Amount in GRAV
 */
export function ugravitonToGrav(ugraviton: string | undefined, decimals = 6): string {
  if (ugraviton === undefined || ugraviton === '' || ugraviton.length === 0) return '0.' + '0'.repeat(decimals);
  try {
    const amount = BigInt(ugraviton);
    const whole = amount / BigInt(1_000_000);
    const fraction = amount % BigInt(1_000_000);
    const fractionStr = fraction.toString().padStart(6, '0').slice(0, decimals);
    return `${whole}.${fractionStr}`;
  } catch {
    return '0.' + '0'.repeat(decimals);
  }
}

/**
 * Create a MsgSendToEth message for bridging tokens to Ethereum
 * 
 * @param params SendToEth parameters (using snake_case)
 * @returns EncodeObject for signing
 */
export function createSendToEthMsg(params: SendToEthParams): EncodeObject {
  const denom = params.denom ?? GRAV_DENOM;
  
  // Note: The proto message fields use camelCase internally
  return {
    typeUrl: '/gravity.v1.MsgSendToEth',
    value: {
      sender: params.sender,
      ethDest: params.eth_dest,
      amount: {
        denom,
        amount: params.amount,
      },
      bridgeFee: {
        denom,
        amount: params.bridge_fee,
      },
      chainFee: {
        denom,
        amount: params.chain_fee,
      },
    },
  };
}

/**
 * Parameters for creating a MsgRequestBatch message
 */
export interface RequestBatchParams {
  sender: string;
  denom: string;
}

/**
 * Create a MsgRequestBatch message for requesting a batch to be created
 * 
 * @param params RequestBatch parameters
 * @returns EncodeObject for signing
 */
export function createRequestBatchMsg(params: RequestBatchParams): EncodeObject {
  return {
    typeUrl: '/gravity.v1.MsgRequestBatch',
    value: {
      sender: params.sender,
      denom: params.denom,
    },
  };
}

/**
 * Validate an Ethereum address
 * @param address Address to validate
 * @returns true if valid Ethereum address
 */
export function isValidEthAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

/**
 * Validate a Gravity Bridge address
 * @param address Address to validate
 * @returns true if valid Gravity address
 */
export function isValidGravityAddress(address: string): boolean {
  return /^gravity1[a-z0-9]{38,45}$/.test(address);
}

/**
 * Format an Ethereum address with ellipsis
 * @param address Full Ethereum address
 * @returns Shortened address (e.g., 0x1234...5678)
 */
export function shortenEthAddress(address: string): string {
  if (address.length < 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
